<template>
  <div>
    <!-- Encabezado de sección -->
    <div class="row align-items-center mb-3 g-2">
      <div class="col-md-8 col-12">
        <h5 class="font-size-15 mb-1 text-primary">
          <i class="mdi mdi-lightning-bolt-outline me-1"></i> Respuestas Rápidas de WhatsApp
        </h5>
        <p class="text-muted font-size-13 mb-0">
          Crea atajos de texto predefinidos con variables personalizables para responder ágilmente a los clientes en el chat.
        </p>
      </div>
      <div class="col-md-4 col-12 text-md-end">
        <button
          type="button"
          class="btn btn-primary btn-sm d-inline-flex align-items-center shadow-sm"
          @click="crearNuevaRespuesta"
        >
          <i class="mdi mdi-plus-circle-outline me-1"></i> Nueva Respuesta Rápida
        </button>
      </div>
    </div>

    <!-- Lista y Editor de Respuestas Rápidas -->
    <div class="row g-4">
      <!-- Columna Izquierda: Listado de Respuestas Rápidas -->
      <div class="col-lg-4 col-md-5">
        <div class="card border h-100 shadow-sm">
          <div class="card-header bg-light d-flex justify-content-between align-items-center py-2 px-3">
            <span class="fw-bold font-size-13 text-secondary">
              <i class="mdi mdi-format-list-bulleted me-1"></i> Atajos creados ({{ respuestas.length }})
            </span>
            <button
              type="button"
              class="btn btn-sm btn-link text-primary p-0 text-decoration-none font-size-12"
              @click="crearNuevaRespuesta"
            >
              <i class="mdi mdi-plus"></i> Crear
            </button>
          </div>

          <div class="card-body p-2" style="max-height: 520px; overflow-y: auto;">
            <div v-if="respuestas.length === 0" class="text-center py-5 px-3 text-muted">
              <i class="mdi mdi-message-flash-outline font-size-36 d-block mb-2 text-secondary opacity-50"></i>
              <div class="fw-semibold font-size-13 mb-1">Sin respuestas rápidas</div>
              <p class="font-size-12 mb-3">Crea tu primer atajo con el botón "Nueva Respuesta Rápida".</p>
              <button
                type="button"
                class="btn btn-outline-primary btn-sm"
                @click="crearNuevaRespuesta"
              >
                <i class="mdi mdi-plus me-1"></i> Crear atajo
              </button>
            </div>

            <div class="list-group list-group-flush" v-else>
              <a
                href="javascript:void(0);"
                v-for="(r, idx) in respuestas"
                :key="r.id || idx"
                class="list-group-item list-group-item-action border rounded-2 mb-2 p-2.5 transition-all"
                :class="{
                  'active-reply-item bg-primary-subtle border-primary text-primary': seleccionIdx === idx,
                  'border-light-subtle': seleccionIdx !== idx
                }"
                @click="seleccionar(idx)"
              >
                <div class="d-flex justify-content-between align-items-start mb-1">
                  <div class="fw-bold font-size-13 text-truncate me-2" :title="r.titulo || 'Sin título'">
                    <i class="mdi mdi-lightning-bolt text-warning me-1"></i>
                    {{ r.titulo || 'Sin título' }}
                  </div>
                  <span class="badge bg-secondary font-monospace font-size-11 flex-shrink-0">
                    /{{ limpiarAtajo(r.atajo) || 'atajo' }}
                  </span>
                </div>
                <div class="text-muted font-size-12 text-truncate line-clamp-2" style="white-space: pre-wrap; max-height: 38px;">
                  {{ r.mensaje || 'Mensaje vacío...' }}
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Columna Derecha: Formulario Editor -->
      <div class="col-lg-8 col-md-7">
        <div class="card border shadow-sm" v-if="respuestaActual">
          <div class="card-header d-flex justify-content-between align-items-center py-2 px-3 bg-light">
            <div class="fw-bold font-size-13 text-primary d-flex align-items-center">
              <i class="mdi mdi-pencil-outline me-1"></i>
              {{ esNuevo ? 'Creando nueva respuesta rápida' : `Editando: ${respuestaActual.titulo || 'Sin título'}` }}
            </div>
            <div class="d-flex align-items-center gap-1">
              <button
                type="button"
                class="btn btn-sm btn-outline-danger py-1 px-2 font-size-12"
                @click="eliminarRespuesta(seleccionIdx)"
                title="Eliminar esta respuesta rápida"
              >
                <i class="mdi mdi-trash-can-outline me-1"></i> Eliminar
              </button>
            </div>
          </div>

          <div class="card-body p-3">
            <div class="row g-3 mb-3">
              <!-- Título descriptivo -->
              <div class="col-md-7 col-12">
                <label class="form-label fw-bold font-size-13 mb-1">
                  Título descriptivo <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="respuestaActual.titulo"
                  placeholder="Ej. Saludo inicial, Recordatorio de pago, etc."
                  maxlength="60"
                  @input="alCambiar"
                />
              </div>

              <!-- Atajo / comando -->
              <div class="col-md-5 col-12">
                <label class="form-label fw-bold font-size-13 mb-1">
                  Atajo en el chat <span class="text-danger">*</span>
                  <small class="text-muted fw-normal font-size-11">(escribe / en el chat)</small>
                </label>
                <div class="input-group input-group-sm">
                  <span class="input-group-text bg-light fw-bold text-primary">/</span>
                  <input
                    type="text"
                    class="form-control form-control-sm font-monospace"
                    :value="limpiarAtajo(respuestaActual.atajo)"
                    @input="actualizarAtajo($event.target.value)"
                    placeholder="saludo, pago, etc."
                    maxlength="30"
                  />
                </div>
              </div>
            </div>

            <!-- Selector de Variables Disponibles -->
            <div class="mb-3 p-2 bg-light rounded-3 border">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <span class="fw-bold font-size-12 text-secondary">
                  <i class="mdi mdi-variable me-1 text-primary"></i> Variables dinámicas (haz clic para insertar en el mensaje)
                </span>
                <span class="badge bg-white text-muted border font-size-10">Se sustituyen con los datos del chat</span>
              </div>

              <div
                v-for="(grupo, idx) in gruposVariables"
                :key="grupo.categoria"
                :class="{ 'mb-2': idx < gruposVariables.length - 1 }"
              >
                <div class="d-flex align-items-center mb-1">
                  <span class="font-size-11 fw-semibold text-dark me-2">
                    <i :class="grupo.icono + ' text-primary me-1'"></i> {{ grupo.categoria }}:
                  </span>
                </div>
                <div class="d-flex flex-wrap gap-1">
                  <button
                    type="button"
                    class="btn btn-sm py-0 px-2 font-size-11 bg-white shadow-2xs"
                    :class="grupo.btnClass"
                    v-for="v in grupo.variables"
                    :key="v.codigo"
                    @click="insertarVariable(v.codigo)"
                    :title="v.descripcion + ' — Clic para insertar ' + v.codigo"
                  >
                    + {{ v.codigo }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Mensaje de la Respuesta Rápida -->
            <div class="mb-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <label class="form-label fw-bold font-size-13 mb-0">
                  <i class="mdi mdi-message-text-outline text-primary me-1"></i> Mensaje de la respuesta rápida <span class="text-danger">*</span>
                </label>
                <span class="badge bg-secondary bg-opacity-10 text-body border font-size-11">
                  {{ respuestaActual.mensaje ? respuestaActual.mensaje.length : 0 }}/1000 caracteres
                </span>
              </div>
              <textarea
                class="form-control font-monospace font-size-13"
                ref="txtMensaje"
                rows="6"
                v-model="respuestaActual.mensaje"
                placeholder="Escribe el texto de la respuesta rápida. Puedes usar las variables de arriba..."
                maxlength="1000"
                @input="alCambiar"
              ></textarea>
              <small class="text-muted font-size-11 mt-1 d-block">
                En la ventana de chat escribe <code>/{{ limpiarAtajo(respuestaActual.atajo) || 'atajo' }}</code> para seleccionarla e insertarla al instante.
              </small>
            </div>

            <!-- Botones de Acción -->
            <div class="d-flex justify-content-between align-items-center pt-2 border-top">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click="restablecerPredeterminados"
                title="Carga atajos sugeridos por defecto"
              >
                <i class="mdi mdi-refresh me-1"></i> Cargar sugerencias
              </button>
              <button
                type="button"
                class="btn btn-sm btn-success px-3 shadow-sm d-inline-flex align-items-center"
                @click="$emit('guardar')"
                :disabled="banderaSpinner"
              >
                <i class="mdi me-1" :class="banderaSpinner ? 'mdi-loading mdi-spin' : 'mdi-content-save'"></i>
                {{ banderaSpinner ? 'Guardando...' : 'Guardar Cambios' }}
              </button>
            </div>
          </div>
        </div>

        <div v-else class="card border p-5 text-center text-muted">
          <i class="mdi mdi-cursor-default-click-outline font-size-36 d-block mb-2 text-secondary opacity-50"></i>
          Selecciona una respuesta rápida a la izquierda o crea una nueva.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export const RESPUESTAS_RAPIDAS_PREDETERMINADAS = [
  {
    id: 'saludo',
    titulo: 'Saludo de bienvenida',
    atajo: 'saludo',
    mensaje: '¡Hola {{nombre}}! Gracias por comunicarte con {{nombre_empresa}}. ¿En qué podemos ayudarte el día de hoy?'
  },
  {
    id: 'datos_cuenta',
    titulo: 'Información de su cuenta',
    atajo: 'cuenta',
    mensaje: 'Estimado/a {{nombre_completo}}, su servicio registrado en {{calle}}, {{colonia}} se encuentra activo.'
  },
  {
    id: 'recordatorio_pago',
    titulo: 'Recordatorio de pago',
    atajo: 'pago',
    mensaje: 'Hola {{nombre}}, le recordamos de {{nombre_empresa}} que puede solicitar información de su cuenta o reportar su pago comunicándose con nosotros. ¡Estamos para servirle!'
  },
  {
    id: 'despedida',
    titulo: 'Despedida y atención',
    atajo: 'gracias',
    mensaje: 'Ha sido un placer atenderle, {{nombre}}. Si necesita más asistencia, quedamos a sus órdenes en {{nombre_empresa}}. ¡Que tenga un excelente día!'
  }
];

export default {
  name: 'CmpRespuestasRapidasWhatsapp',
  props: {
    respuestas: {
      type: Array,
      default: () => []
    },
    gruposVariables: {
      type: Array,
      default: () => []
    },
    banderaSpinner: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:respuestas', 'guardar'],
  data() {
    return {
      seleccionIdx: 0,
      esNuevo: false
    };
  },
  computed: {
    respuestaActual() {
      if (!this.respuestas || this.respuestas.length === 0) return null;
      return this.respuestas[this.seleccionIdx] || null;
    }
  },
  watch: {
    respuestas: {
      handler(val) {
        if (val && val.length > 0 && (this.seleccionIdx < 0 || this.seleccionIdx >= val.length)) {
          this.seleccionIdx = 0;
        }
      },
      immediate: true
    }
  },
  methods: {
    limpiarAtajo(str) {
      if (!str) return '';
      return String(str).replace(/^\/+/, '').replace(/\s+/g, '-').toLowerCase();
    },
    actualizarAtajo(val) {
      if (!this.respuestaActual) return;
      this.respuestaActual.atajo = this.limpiarAtajo(val);
      this.alCambiar();
    },
    seleccionar(idx) {
      this.seleccionIdx = idx;
      this.esNuevo = false;
    },
    crearNuevaRespuesta() {
      const nueva = {
        id: 'rr_' + Date.now(),
        titulo: 'Nueva respuesta',
        atajo: 'atajo' + (this.respuestas.length + 1),
        mensaje: 'Hola {{nombre}}, '
      };
      const copia = [...this.respuestas, nueva];
      this.seleccionIdx = copia.length - 1;
      this.esNuevo = true;
      this.$emit('update:respuestas', copia);
      this.$nextTick(() => {
        if (this.$refs.txtMensaje) {
          this.$refs.txtMensaje.focus();
        }
      });
    },
    eliminarRespuesta(idx) {
      if (idx < 0 || idx >= this.respuestas.length) return;
      const copia = [...this.respuestas];
      copia.splice(idx, 1);
      this.seleccionIdx = Math.max(0, idx - 1);
      this.$emit('update:respuestas', copia);
    },
    restablecerPredeterminados() {
      const predeterminados = JSON.parse(JSON.stringify(RESPUESTAS_RAPIDAS_PREDETERMINADAS));
      this.seleccionIdx = 0;
      this.esNuevo = false;
      this.$emit('update:respuestas', predeterminados);
    },
    insertarVariable(codigo) {
      if (!this.respuestaActual) return;
      const textarea = this.$refs.txtMensaje;
      const textoActual = this.respuestaActual.mensaje || '';

      if (!textarea) {
        this.respuestaActual.mensaje = textoActual ? textoActual + ' ' + codigo : codigo;
        this.alCambiar();
        return;
      }

      const inicio = textarea.selectionStart || textoActual.length;
      const fin = textarea.selectionEnd || textoActual.length;

      const antes = textoActual.substring(0, inicio);
      const despues = textoActual.substring(fin, textoActual.length);

      const espacioAntes = antes.length > 0 && !antes.endsWith(' ') ? ' ' : '';
      const espacioDespues = despues.length > 0 && !despues.startsWith(' ') ? ' ' : '';

      this.respuestaActual.mensaje = antes + espacioAntes + codigo + espacioDespues + despues;
      this.alCambiar();

      this.$nextTick(() => {
        const nuevaPos = inicio + espacioAntes.length + codigo.length + espacioDespues.length;
        textarea.focus();
        textarea.setSelectionRange(nuevaPos, nuevaPos);
      });
    },
    alCambiar() {
      this.$emit('update:respuestas', [...this.respuestas]);
    }
  }
};
</script>

<style scoped>
.active-reply-item {
  border-left: 3px solid #008069 !important;
}
</style>

